/**
 * Map tile URLs - the one place every raster base map in the admin client reads from.
 *
 * Before this, the same Google / OpenStreetMap / Esri / CARTO / Stadia URLs were typed
 * out in ~38 components, so switching provider (an official Google Maps key, a self-
 * hosted tile server) meant hunting through all of them.
 *
 * Every URL can be overridden at build time with a VITE_TILE_* variable in
 * .env.development / .env.production, e.g.
 *   VITE_TILE_GOOGLE_SATELLITE=https://tiles.example.gov.in/satellite/{z}/{x}/{y}.png
 * Server variants (Google mt0-mt3, OSM a/b/c, CARTO a/b/c) follow their family's
 * override unless given one of their own, so one variable moves the whole family.
 *
 * Not tiles, so not here: attribution links and the Nominatim reverse-geocoding URL.
 * The app's own vector tiles are served by the API (/gis/tiles/...), see gisService.
 */

const env = import.meta.env as Record<string, string | undefined>;

/** The override for `name`, else the fallback. Blank values count as unset. */
const pick = (name: string, fallback: string): string => {
  const v = env[name];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : fallback;
};

// ─── Family bases (each overridable) ───
const OSM = pick('VITE_TILE_OSM', 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png');
const GOOGLE_ROADS = pick('VITE_TILE_GOOGLE_ROADS', 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}');
const GOOGLE_SATELLITE = pick('VITE_TILE_GOOGLE_SATELLITE', 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}');
const GOOGLE_HYBRID = pick('VITE_TILE_GOOGLE_HYBRID', 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}');
const GOOGLE_TERRAIN = pick('VITE_TILE_GOOGLE_TERRAIN', 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}');
const CARTO_VOYAGER = pick('VITE_TILE_CARTO_VOYAGER', 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png');

/**
 * A family member: its own override, else - when the family base was overridden -
 * the base itself (a custom server rarely has the same mirrors), else the default.
 */
const variant = (name: string, familyVar: string, familyValue: string, fallback: string): string =>
  pick(name, env[familyVar]?.trim() ? familyValue : fallback);

export const TILE_URLS = {
  // OpenStreetMap
  osm: OSM,
  osmPlain: variant('VITE_TILE_OSM_PLAIN', 'VITE_TILE_OSM', OSM, 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'),
  osmA: variant('VITE_TILE_OSM_A', 'VITE_TILE_OSM', OSM, 'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'),
  osmB: variant('VITE_TILE_OSM_B', 'VITE_TILE_OSM', OSM, 'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png'),
  osmC: variant('VITE_TILE_OSM_C', 'VITE_TILE_OSM', OSM, 'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'),

  // Google
  googleRoads: GOOGLE_ROADS,
  googleTerrain: GOOGLE_TERRAIN,
  googleSatellite: GOOGLE_SATELLITE,
  googleSatelliteMt0: variant('VITE_TILE_GOOGLE_SATELLITE_MT0', 'VITE_TILE_GOOGLE_SATELLITE', GOOGLE_SATELLITE, 'https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'),
  googleSatelliteMt2: variant('VITE_TILE_GOOGLE_SATELLITE_MT2', 'VITE_TILE_GOOGLE_SATELLITE', GOOGLE_SATELLITE, 'https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'),
  googleSatelliteMt3: variant('VITE_TILE_GOOGLE_SATELLITE_MT3', 'VITE_TILE_GOOGLE_SATELLITE', GOOGLE_SATELLITE, 'https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'),
  /** Leaflet subdomain form ({s} = 0-3). */
  googleSatelliteSub: variant('VITE_TILE_GOOGLE_SATELLITE_SUB', 'VITE_TILE_GOOGLE_SATELLITE', GOOGLE_SATELLITE, 'https://mt{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}'),
  googleHybrid: GOOGLE_HYBRID,
  googleHybridMt0: variant('VITE_TILE_GOOGLE_HYBRID_MT0', 'VITE_TILE_GOOGLE_HYBRID', GOOGLE_HYBRID, 'https://mt0.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'),
  googleHybridMt2: variant('VITE_TILE_GOOGLE_HYBRID_MT2', 'VITE_TILE_GOOGLE_HYBRID', GOOGLE_HYBRID, 'https://mt2.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'),
  googleHybridMt3: variant('VITE_TILE_GOOGLE_HYBRID_MT3', 'VITE_TILE_GOOGLE_HYBRID', GOOGLE_HYBRID, 'https://mt3.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'),
  /** Leaflet subdomain form ({s} = 0-3). */
  googleHybridSub: variant('VITE_TILE_GOOGLE_HYBRID_SUB', 'VITE_TILE_GOOGLE_HYBRID', GOOGLE_HYBRID, 'https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}'),

  // Esri
  esriImagery: pick('VITE_TILE_ESRI_IMAGERY', 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'),
  esriStreet: pick('VITE_TILE_ESRI_STREET', 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}'),
  esriTopo: pick('VITE_TILE_ESRI_TOPO', 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}'),
  esriReferenceOverlay: pick('VITE_TILE_ESRI_REFERENCE', 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Reference_Overlay/MapServer/tile/{z}/{y}/{x}'),

  // OpenTopoMap
  openTopo: pick('VITE_TILE_OPENTOPO', 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'),

  // CARTO
  cartoVoyager: CARTO_VOYAGER,
  cartoVoyagerA: variant('VITE_TILE_CARTO_VOYAGER_A', 'VITE_TILE_CARTO_VOYAGER', CARTO_VOYAGER, 'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png'),
  cartoVoyagerB: variant('VITE_TILE_CARTO_VOYAGER_B', 'VITE_TILE_CARTO_VOYAGER', CARTO_VOYAGER, 'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png'),
  cartoVoyagerC: variant('VITE_TILE_CARTO_VOYAGER_C', 'VITE_TILE_CARTO_VOYAGER', CARTO_VOYAGER, 'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png'),
  /** Retina-aware form ({r} = "@2x" on high-DPI screens). */
  cartoVoyagerRetina: variant('VITE_TILE_CARTO_VOYAGER_RETINA', 'VITE_TILE_CARTO_VOYAGER', CARTO_VOYAGER, 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png'),
  cartoLight: pick('VITE_TILE_CARTO_LIGHT', 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'),
  cartoDark: pick('VITE_TILE_CARTO_DARK', 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'),

  // Stadia
  stadiaOsmBright: pick('VITE_TILE_STADIA_OSM_BRIGHT', 'https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}@2x.png'),
} as const;

export type TileUrlKey = keyof typeof TILE_URLS;
